// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import {
  ChatResultCard, MakeReadyCard, AuditAttachmentsCard, AddStrandCard, DeltaWatchCard,
} from './chatbotTypes';

// Renders a Khora result card (make_ready / audit_attachments / add_strand / delta_watch) as a badge/table/
// summary. Pure + presentational, mirroring WidgetView: the parent bubble supplies the dark card container +
// the title header; this renders the body and branches on the `type` discriminant. White-on-dark throughout.

const RATING_COLOR: Record<string, string> = {
  green: '#2E7D32', yellow: '#F9A825', red: '#D32F2F', gray: '#9E9E9E', unsure: '#9E9E9E',
};
const SEVERITY_COLOR: Record<string, string> = { high: '#D32F2F', medium: '#F9A825', low: '#9E9E9E' };
// Ghost (seen-but-not-recorded) is the confirmed, actionable discrepancy → the attention color. Missing
// (recorded-but-not-seen) is occlusion-prone and less certain → a calmer informational tone, NOT danger-red,
// so it doesn't read as a field-verified absence (see the audit card caveat).
const GHOST_COLOR = '#F9A825';
const MISSING_COLOR = '#408BBB';

const cellSx = { color: '#eee', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.1)', whiteSpace: 'nowrap' } as const;
const headSx = { color: 'grey.400', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.1)', whiteSpace: 'nowrap' } as const;
const mutedSx = { fontSize: '0.72rem', color: 'grey.400' } as const;
const textSx = { fontSize: '0.72rem', color: '#eee', overflowWrap: 'anywhere' as const };

// A small colored pill — the codebase renders these as a styled Box, not an MUI Chip. Exported so the
// StoryboardPlayer reuses the same badge look.
export const Pill: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block', px: 0.8, py: 0.2, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
      color: 'common.white', backgroundColor: color, whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

// A label:value line — renders nothing when the value is empty, so absent fields leave no blank row.
const KeyRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography sx={{ ...mutedSx, minWidth: 96, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={textSx}>{value}</Typography>
    </Box>
  );
};

const MakeReadyBody: React.FC<{ card: MakeReadyCard }> = ({ card }) => {
  const rating = (card.rating || 'unsure').toLowerCase();
  const seen = Object.entries(card.vision_counts ?? {})
    .filter(([, count]) => typeof count === 'number' && count > 0)
    .map(([kind, count]) => `${count} ${kind.replace(/_/g, ' ')}`);
  // The rating is a blind single-frame vision estimate; the RECORD may still flag a make-ready item. Surface
  // that contradiction so a green/yellow badge can't be read as "clear" over a real record issue.
  const ratingLooksClear = rating === 'green' || rating === 'yellow';
  const hasHighPunch = !!card.punch_list?.some((item) => item.severity === 'high');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Pill color={RATING_COLOR[rating] ?? '#9E9E9E'}>{rating.toUpperCase()}</Pill>
        <Typography sx={textSx}>Pole {String(card.pole_id)}</Typography>
        <Typography sx={mutedSx}>(visual estimate)</Typography>
      </Box>
      {card.reasoning && <Typography sx={textSx}>{card.reasoning}</Typography>}
      <KeyRow label="Condition" value={card.condition} />
      <KeyRow label="Type" value={card.pole_type} />
      <KeyRow label="Material" value={card.material} />
      {seen.length > 0 && <KeyRow label="Seen in photo" value={seen.join(' · ')} />}
      {ratingLooksClear && hasHighPunch && (
        <Typography sx={{ ...mutedSx, color: '#F9A825' }}>
          Heads up: the record flags a make-ready item despite the visual rating — see below.
        </Typography>
      )}
      {!!card.punch_list?.length && (
        <Box>
          <Typography sx={{ ...mutedSx, mt: 0.5 }}>Make-ready items (from the record)</Typography>
          <Table size="small">
            <TableBody>
              {card.punch_list.map((item, index) => {
                // Content + position: two punch items can be content-identical (e.g. two comms at the same
                // rounded height), so index disambiguates. Not an index-ONLY key (the list is static per render).
                const rowKey = `${item.severity}|${item.issue}|${index}`;
                return (
                  <TableRow key={rowKey}>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'normal' }}>
                      <Pill color={SEVERITY_COLOR[item.severity] ?? '#9E9E9E'}>{item.severity}</Pill>
                      {' '}{item.issue} — <em>{item.action}</em>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      {card.additional_notes && <Typography sx={mutedSx}>{card.additional_notes}</Typography>}
      {card.caveat && <Typography sx={mutedSx}>{card.caveat}</Typography>}
    </Box>
  );
};

const AuditAttachmentsBody: React.FC<{ card: AuditAttachmentsCard }> = ({ card }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
      <Typography sx={textSx}>Pole {String(card.pole_id)}</Typography>
      <Pill color={GHOST_COLOR}>{card.total_ghost} ghost</Pill>
      <Pill color={MISSING_COLOR}>{card.total_missing} missing</Pill>
    </Box>
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Type', 'Recorded', 'Observed', 'Ghost', 'Missing'].map((column) => (
              <TableCell key={column} sx={headSx}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {card.rows.map((row) => (
            <TableRow key={row.type}>
              <TableCell sx={cellSx}>{row.type}</TableCell>
              <TableCell sx={cellSx}>{row.recorded}</TableCell>
              <TableCell sx={cellSx}>{row.observed}</TableCell>
              <TableCell sx={{ ...cellSx, color: row.ghost ? GHOST_COLOR : '#eee' }}>{row.ghost}</TableCell>
              <TableCell sx={{ ...cellSx, color: row.missing ? MISSING_COLOR : '#eee' }}>{row.missing}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    {card.notes?.map((note) => <Typography key={note} sx={mutedSx}>{note}</Typography>)}
    {card.caveat && <Typography sx={mutedSx}>{card.caveat}</Typography>}
  </Box>
);

const AddStrandBody: React.FC<{ card: AddStrandCard }> = ({ card }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Pill color={card.feasible ? '#2E7D32' : '#D32F2F'}>{card.feasible ? 'Room for a strand' : 'No room'}</Pill>
      <Typography sx={textSx}>Pole {String(card.pole_id)}</Typography>
    </Box>
    {typeof card.recommended_height_ft === 'number'
      && <KeyRow label="Recommended" value={`${card.recommended_height_ft.toFixed(1)} ft`} />}
    {!!card.windows_text?.length && <KeyRow label="Feasible window(s)" value={card.windows_text.join('; ')} />}
    {card.proposed && (
      <KeyRow
        label={`At ${card.proposed.height_ft.toFixed(1)} ft`}
        value={card.proposed.feasible ? 'feasible' : `not feasible — ${card.proposed.conflicts.join('; ')}`}
      />
    )}
    {card.note && <Typography sx={mutedSx}>{card.note}</Typography>}
  </Box>
);

// Join a capped detail list with its EXACT total, disclosing truncation: "3, 7, 12 (+5 more)".
const cappedLine = (parts: string[], total: number): string | null => {
  if (!parts.length) return null;
  const extra = total - parts.length;
  return extra > 0 ? `${parts.join(', ')} (+${extra} more)` : parts.join(', ');
};

// A screening count below the flat minimum is NOT an adjudicated NESC determination — say so (audit_clearances
// carries the cited clause; the delta card only reports the change, so it gets the plain screening caveat).
const DELTA_CLEARANCE_CAVEAT = 'Clearance figures are a screening count below the flat minimum, not an adjudicated NESC determination.';

const DeltaWatchBody: React.FC<{ card: DeltaWatchCard }> = ({ card }) => {
  const { poles, clearance_delta: clearance } = card;
  const signed = (value: number): string => `${value > 0 ? '+' : ''}${value}`;
  const added = cappedLine((card.added_poles ?? []).map(String), poles.added);
  const removed = cappedLine((card.removed_poles ?? []).map(String), poles.removed);
  const moved = cappedLine(
    (card.moved_poles ?? []).map((m) => (typeof m.moved_ft === 'number' ? `${m.pole_id} (${m.moved_ft.toFixed(1)} ft)` : String(m.pole_id))),
    poles.moved,
  );
  const heightChanged = cappedLine(
    (card.height_changed_poles ?? []).map((h) => (typeof h.delta_ft === 'number' ? `${h.pole_id} (${signed(h.delta_ft)} ft)` : String(h.pole_id))),
    poles.height_changed,
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
        <Pill color="#2E7D32">{`+${poles.added} added`}</Pill>
        <Pill color="#D32F2F">{`${poles.removed} removed`}</Pill>
        <Pill color="#F9A825">{`${poles.moved} moved`}</Pill>
        <Pill color="#408BBB">{`${poles.height_changed} height-changed`}</Pill>
      </Box>
      <KeyRow label="Added" value={added} />
      <KeyRow label="Removed" value={removed} />
      <KeyRow label="Moved" value={moved} />
      <KeyRow label="Height-changed" value={heightChanged} />
      {clearance && (
        <KeyRow
          label={`Clearances < ${clearance.min_ft} ft`}
          value={`${clearance.prior} → ${clearance.current} (Δ ${signed(clearance.delta)})`}
        />
      )}
      {clearance && <Typography sx={mutedSx}>{DELTA_CLEARANCE_CAVEAT}</Typography>}
      {!!card.layer_delta?.length && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Layer', 'Prior', 'Current', 'Δ'].map((column) => (
                  <TableCell key={column} sx={headSx}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {card.layer_delta.map((row) => (
                <TableRow key={row.layer}>
                  <TableCell sx={cellSx}>{row.layer}</TableCell>
                  <TableCell sx={cellSx}>{row.prior}</TableCell>
                  <TableCell sx={cellSx}>{row.current}</TableCell>
                  <TableCell sx={{ ...cellSx, color: row.delta > 0 ? '#2E7D32' : (row.delta < 0 ? '#D32F2F' : '#eee') }}>
                    {signed(row.delta)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {card.highlight_truncated
        && <Typography sx={mutedSx}>Map shows a capped subset of the changed poles; the counts above are exact.</Typography>}
    </Box>
  );
};

const ChatCard: React.FC<{ card: ChatResultCard }> = ({ card }) => {
  switch (card.type) {
    case 'make_ready': return <MakeReadyBody card={card} />;
    case 'audit_attachments': return <AuditAttachmentsBody card={card} />;
    case 'add_strand': return <AddStrandBody card={card} />;
    case 'delta_watch': return <DeltaWatchBody card={card} />;
    default: return null;
  }
};

export default ChatCard;
